import { Interface } from 'ethers'

import IERC20 from '../../../contracts/compiled/IERC20.json'
import ErrorHumanizerError from '../../classes/ErrorHumanizerError'
import { RPCProvider } from '../../interfaces/provider'
import { AccountOp } from '../accountOp/accountOp'
import { TOKEN_BALANCE_TOO_LOW_REASON, tokenBalanceTooLow } from '../errorHumanizer/errors'
import { MESSAGE_PREFIX } from '../errorHumanizer/estimationErrorHumanizer'

const erc20 = new Interface(IERC20.abi)
const TRANSFER_SELECTOR = erc20.getFunction('transfer')!.selector

/** Sum of the ERC-20 `transfer` amounts in the op, per token address. */
const getTransferTotals = (op: AccountOp): Map<string, bigint> => {
  const totals = new Map<string, bigint>()
  op.calls.forEach((call) => {
    if (!call.to || !call.data?.startsWith(TRANSFER_SELECTOR)) return
    try {
      const [, amount] = erc20.decodeFunctionData('transfer', call.data)
      const token = call.to.toLowerCase()
      totals.set(token, (totals.get(token) ?? 0n) + BigInt(amount))
    } catch {
      // not a well-formed transfer(address,uint256): leave it to the estimation error
    }
  })
  return totals
}

/**
 * A token transfer that reverts at estimation usually means the account holds
 * less than the amount, but tokens such as WPLS revert with no data
 * (`require(balanceOf >= wad)`), which the humanizer can only call "reason
 * unknown". The form validated the amount against the portfolio balance, which
 * can be stale (a DCA order may have just spent the token), so read the
 * balance onchain and return a specific error when it is too low.
 *
 * Returns null when every transfer is covered, or when the check itself fails.
 */
export async function getTokenBalanceTooLowError(
  op: AccountOp,
  provider: RPCProvider
): Promise<ErrorHumanizerError | null> {
  const totals = getTransferTotals(op)
  if (!totals.size) return null

  try {
    const balances = await Promise.all(
      [...totals.keys()].map(async (token) => {
        const data = erc20.encodeFunctionData('balanceOf', [op.accountAddr])
        const result = await provider.call({ to: token, data })
        return [token, BigInt(erc20.decodeFunctionResult('balanceOf', result)[0])] as const
      })
    )
    const isTooLow = balances.some(([token, balance]) => balance < totals.get(token)!)
    if (!isTooLow) return null
  } catch {
    return null
  }

  return new ErrorHumanizerError(`${MESSAGE_PREFIX} ${tokenBalanceTooLow}`, {
    cause: TOKEN_BALANCE_TOO_LOW_REASON,
    isFallbackMessage: false
  })
}
